import { create } from 'zustand';
import { AlertasService } from '../services/alertas.service';
import { AlertaUi } from '../schemas/alertas/listar-alertas';
import { useLoadingStore } from './loadingStore';
import { useAlertStore } from './alertStore';

export interface Notification {
  id: string;
  title: string;
  description: string;
  date: string;
  isRead: boolean;
}

interface FetchOptions {
  silent?: boolean;
  page?: number;
  limit?: number;
}

interface NotificationsState {
  notifications: Notification[];
  unreadNotifications: Notification[];
  page: number;
  limit: number;
  total: number;
  fetchNotifications: (options?: FetchOptions) => Promise<void>;
  fetchUnreadNotifications: (limit?: number) => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  getUnreadCount: () => number;
  setPagination: (page: number, limit: number, total: number) => void;
}

const transformAlertaToNotification = (alerta: AlertaUi): Notification => ({
  id: alerta.id,
  title: alerta.encabezado,
  description: alerta.descripcion,
  date: alerta.fecha_creacion_alerta,
  isRead: alerta.isRead
});

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  notifications: [],
  unreadNotifications: [],
  page: 1,
  limit: 20,
  total: 0,
  
  setPagination: (page, limit, total) => set({ page, limit, total }),
  
  fetchNotifications: async (options: FetchOptions = {}) => {
    const { silent = false, page, limit } = options;
    const currentState = get();
    const pageToUse = page ?? currentState.page;
    const limitToUse = limit ?? currentState.limit;
    
    const setLoading = useLoadingStore.getState().setLoading;
    const showAlert = useAlertStore.getState().showAlert;

    if (!silent) {
      setLoading(true);
    }
    
    try {
      const result = await AlertasService.listarAlertas(pageToUse, limitToUse);
      const notifications = result.data.map(transformAlertaToNotification);
      
      set({ 
        notifications: notifications,
        page: result.page,
        limit: result.limit,
        total: result.total
      });
      
      if (!silent) {
        showAlert('Notificaciones cargadas exitosamente', 'success');
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error al cargar las notificaciones';
      showAlert(errorMessage, 'error');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  },
  
  fetchUnreadNotifications: async (limit: number = 50) => {
    const setLoading = useLoadingStore.getState().setLoading;
    
    try {
      setLoading(true);
      
      const result = await AlertasService.listarAlertas(1, limit);
      const allNotifications = result.data.map(transformAlertaToNotification);
      
      const unreadNotifications = allNotifications
        .filter(notification => !notification.isRead)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      
      set({ unreadNotifications });
    } catch (error) {
      console.error('Error al obtener notificaciones no leídas:', error);
    } finally {
      setLoading(false);
    }
  },
  
  markAsRead: async (id: string) => {
    set((state) => ({
      notifications: state.notifications.map((notification) =>
        notification.id === id ? { ...notification, isRead: true } : notification
      ),
      unreadNotifications: state.unreadNotifications.filter(
        notification => notification.id !== id
      )
    }));
    
    try {
      await AlertasService.marcarComoVisualizada(id);
    } catch (error) {
      set((state) => ({
        notifications: state.notifications.map((notification) =>
          notification.id === id ? { ...notification, isRead: false } : notification
        )
      }));
      
      const { fetchUnreadNotifications } = get();
      await fetchUnreadNotifications();
      
      const { showAlert } = useAlertStore.getState();
      const errorMessage = error instanceof Error ? error.message : 'Error al marcar la notificación como leída';
      showAlert(errorMessage, 'error');
    }
  },
  
  getUnreadCount: () => {
    const { unreadNotifications, notifications } = get();
    if (unreadNotifications.length > 0) {
      return unreadNotifications.length;
    }
    return notifications.filter(notification => !notification.isRead).length;
  }
})); 